'use client';

import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Link, useParams, Navigate } from 'react-router-dom';
import TitleBar from '../layout/TitleBar';
import { useBusinessUnitsAll } from '@/hooks/api/useBusinessUnits';
import {
  useDeleteBusinessUnitIcon,
  useDeleteBusinessUnitLogo,
  useUploadBusinessUnitIcon,
  useUploadBusinessUnitLogo,
  useUpdateBusinessUnitName,
  usePatchBusinessUnitCountCalendarTimezone,
} from '@/hooks/api/useBusinessUnitBranding';
import { useSnackbar } from 'notistack';
import { BUSINESS_UNIT_COUNT_CALENDAR_TIMEZONE_OPTIONS } from '@/utils/buCalendar';

const LOGO_HELP =
  'Recommended: wide horizontal logo, about 480×120 px (4:1) or 400×100 px. Use PNG or WebP; keep under ~500 KB. Shown in the top app bar and expanded sidebar.';

const ICON_HELP =
  'Square mark for the business unit list and for the sidebar when it is collapsed (about 128×128 px to 256×256 px, PNG or WebP). If omitted, the horizontal logo is used as a fallback in those places.';

/**
 * Per–business-unit admin screen: display name and logo (code is not editable).
 * Route: /app/business-units/:code/setup
 */
export default function BusinessUnitSetupPage() {
  const { code } = useParams<{ code: string }>();
  const { data: units = [], isLoading, isError } = useBusinessUnitsAll(true);
  const bu = code ? units.find((u) => u.code === code) : undefined;

  const [nameDraft, setNameDraft] = useState('');
  const upload = useUploadBusinessUnitLogo();
  const remove = useDeleteBusinessUnitLogo();
  const uploadIcon = useUploadBusinessUnitIcon();
  const removeIcon = useDeleteBusinessUnitIcon();
  const updateName = useUpdateBusinessUnitName();
  const patchTimezone = usePatchBusinessUnitCountCalendarTimezone();
  const { enqueueSnackbar } = useSnackbar();
  const inputRef = useRef<HTMLInputElement>(null);
  const iconInputRef = useRef<HTMLInputElement>(null);
  const [timezoneDraft, setTimezoneDraft] = useState('America/Denver');

  useEffect(() => {
    if (bu) setNameDraft(bu.name);
  }, [bu?.id, bu?.name]);

  useEffect(() => {
    if (bu?.countCalendarTimeZone) {
      setTimezoneDraft(bu.countCalendarTimeZone);
    }
  }, [bu?.id, bu?.countCalendarTimeZone]);

  if (!code) {
    return <Navigate to="/app/business-units" replace />;
  }

  if (isLoading) {
    return (
      <Box>
        <TitleBar
          title="Business unit setup"
          breadcrumbs={[
            { label: 'Business units', href: '/app/business-units' },
            { label: 'Setup' },
          ]}
        />
        <CircularProgress size={32} sx={{ mt: 2 }} />
      </Box>
    );
  }

  if (isError || !bu) {
    return (
      <Box>
        <TitleBar
          title="Business unit setup"
          breadcrumbs={[
            { label: 'Business units', href: '/app/business-units' },
            { label: 'Setup' },
          ]}
        />
        <Typography color="error" sx={{ mt: 2 }}>
          {isError ? 'Unable to load business units.' : 'Unknown business unit.'}
        </Typography>
        <Button component={Link} to="/app/business-units" sx={{ mt: 1 }}>
          Back to business units
        </Button>
      </Box>
    );
  }

  const nameChanged = nameDraft.trim() !== bu.name;
  const timezoneChanged = timezoneDraft !== (bu.countCalendarTimeZone ?? 'America/Denver');
  const busy =
    upload.isPending ||
    remove.isPending ||
    uploadIcon.isPending ||
    removeIcon.isPending ||
    updateName.isPending ||
    patchTimezone.isPending;

  const saveName = () => {
    const next = nameDraft.trim();
    if (!next) {
      enqueueSnackbar('Name is required.', { variant: 'warning' });
      return;
    }
    updateName.mutate(
      { id: bu.id, name: next },
      {
        onSuccess: () => enqueueSnackbar('Name saved.', { variant: 'success' }),
        onError: (e: Error) =>
          enqueueSnackbar(e.message || 'Could not save name', { variant: 'error' }),
      },
    );
  };

  const onIconFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      enqueueSnackbar('Please choose an image file.', { variant: 'warning' });
      return;
    }
    uploadIcon.mutate(
      { businessUnitId: bu.id, file },
      {
        onSuccess: () => enqueueSnackbar('Square icon updated.', { variant: 'success' }),
        onError: (err: Error) =>
          enqueueSnackbar(err.message || 'Upload failed', { variant: 'error' }),
      },
    );
  };

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      enqueueSnackbar('Please choose an image file.', { variant: 'warning' });
      return;
    }
    upload.mutate(
      { businessUnitId: bu.id, file },
      {
        onSuccess: () => enqueueSnackbar('Logo updated.', { variant: 'success' }),
        onError: (err: Error) =>
          enqueueSnackbar(err.message || 'Upload failed', { variant: 'error' }),
      },
    );
  };

  const saveTimezone = () => {
    patchTimezone.mutate(
      { businessUnitId: bu.id, countCalendarTimeZone: timezoneDraft },
      {
        onSuccess: () => enqueueSnackbar('Count calendar time zone saved.', { variant: 'success' }),
        onError: (e: Error) =>
          enqueueSnackbar(e.message || 'Could not save time zone', { variant: 'error' }),
      },
    );
  };

  return (
    <Box>
      <TitleBar
        title="Business unit setup"
        breadcrumbs={[
          { label: 'Business units', href: '/app/business-units' },
          { label: bu.name },
        ]}
      />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
        Internal code <strong>{bu.code}</strong> is fixed. Update the display name, header logo,
        and optional square icon.
      </Typography>

      <Card variant="outlined" sx={{ maxWidth: 560 }}>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Display name
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            disabled={busy}
            sx={{ mb: 2 }}
            slotProps={{ htmlInput: { maxLength: 200 } }}
          />
          <Button
            variant="contained"
            size="small"
            disabled={busy || !nameChanged}
            onClick={saveName}
          >
            Save name
          </Button>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ maxWidth: 560, mt: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Count calendar time zone
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Inventory Auditor count days and Tom&apos;s Vending visit days roll at midnight in this zone. Store counts
            and visit tallies start at zero each new calendar day.
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="bu-tz-label">Time zone</InputLabel>
            <Select
              labelId="bu-tz-label"
              label="Time zone"
              value={timezoneDraft}
              disabled={busy}
              onChange={(e) => setTimezoneDraft(String(e.target.value))}
            >
              {BUSINESS_UNIT_COUNT_CALENDAR_TIMEZONE_OPTIONS.map((o) => (
                <MenuItem key={o.id} value={o.id}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" size="small" disabled={busy || !timezoneChanged} onClick={saveTimezone}>
            Save time zone
          </Button>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ maxWidth: 560, mt: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Square icon
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {ICON_HELP}
          </Typography>
          <Box
            sx={{
              width: 120,
              height: 120,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'action.hover',
              borderRadius: 1,
              mb: 2,
              overflow: 'hidden',
            }}
          >
            {bu.iconUrl ? (
              <Box
                component="img"
                src={bu.iconUrl}
                alt=""
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <Typography variant="caption" color="text.secondary" align="center" sx={{ px: 1 }}>
                Not set
              </Typography>
            )}
          </Box>
          <input
            ref={iconInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            hidden
            onChange={onIconFile}
          />
          <Box display="flex" gap={1} flexWrap="wrap" sx={{ mb: 0 }}>
            <Button
              size="small"
              variant="contained"
              disabled={busy}
              onClick={() => iconInputRef.current?.click()}
            >
              Upload icon
            </Button>
            <Button
              size="small"
              color="inherit"
              disabled={busy || !bu.iconUrl}
              onClick={() =>
                removeIcon.mutate(bu.id, {
                  onSuccess: () =>
                    enqueueSnackbar('Square icon removed.', { variant: 'success' }),
                  onError: (e: Error) =>
                    enqueueSnackbar(e.message || 'Remove failed', { variant: 'error' }),
                })
              }
            >
              Remove icon
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ maxWidth: 560, mt: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Header logo
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {LOGO_HELP}
          </Typography>
          <Box
            sx={{
              height: 88,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'action.hover',
              borderRadius: 1,
              mb: 2,
              overflow: 'hidden',
            }}
          >
            {bu.logoUrl ? (
              <Box
                component="img"
                src={bu.logoUrl}
                alt=""
                sx={{ maxHeight: 80, maxWidth: '100%', objectFit: 'contain' }}
              />
            ) : (
              <Typography variant="caption" color="text.secondary">
                No logo — a default image is used in the header
              </Typography>
            )}
          </Box>
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            hidden
            onChange={onFile}
          />
          <Box display="flex" gap={1} flexWrap="wrap">
            <Button
              size="small"
              variant="contained"
              disabled={busy}
              onClick={() => inputRef.current?.click()}
            >
              Upload logo
            </Button>
            <Button
              size="small"
              color="inherit"
              disabled={busy || !bu.logoUrl}
              onClick={() =>
                remove.mutate(bu.id, {
                  onSuccess: () =>
                    enqueueSnackbar('Logo removed.', { variant: 'success' }),
                  onError: (e: Error) =>
                    enqueueSnackbar(e.message || 'Remove failed', { variant: 'error' }),
                })
              }
            >
              Remove logo
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
